// src/features/settings/hooks/useCompanySettings.js
import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
    fetchCompanySettings,
    updateCompanySettings,
    uploadCompanyImage,
    deleteCompanyImage,
    fetchSampleContract,
    fetchCompanyLogo,
    fetchCompanyFavicon
} from "../../../api/requests/company-requests";
import { describeFetchError } from "../../../utils/apiErrors";
import { BRANDING_LABELS, validateBrandingFile } from "../../../constants/brandingAssets";

export const useCompanySettings = (companyId) => {
    const [loading, setLoading] = useState(false);
    const [sampleLoading, setSampleLoading] = useState(false);

    // Fatura Tipi Kontrolü (TC vs Vergi No)
    const [isCorporate, setIsCorporate] = useState(true);

    // Resim State'leri
    const [logoBlobUrl, setLogoBlobUrl] = useState(null);
    const [faviconBlobUrl, setFaviconBlobUrl] = useState(null);
    const [logoError, setLogoError] = useState(false);
    const [faviconError, setFaviconError] = useState(false);

    const navigate = useNavigate();

    // --- Resim İşlemleri ---

    /**
     * Blob URL'ini değiştirirken eskisini serbest bırakır.
     *
     * Önceden hiçbir yerde revokeObjectURL çağrılmıyordu: her yenilemede bir blob
     * daha birikiyor ve sekme açık kaldıkça bellek büyüyordu. Fonksiyonel setter
     * kullanılıyor çünkü önceki değeri closure'dan okumak bayat veri riski taşır.
     */
    const swapBlobUrl = (setter, nextUrl) => {
        setter((prevUrl) => {
            if (prevUrl && prevUrl !== nextUrl) URL.revokeObjectURL(prevUrl);
            return nextUrl;
        });
    };

    const loadImages = async () => {
        if (!companyId) return;

        // Logo Çekme
        try {
            const resLogo = await fetchCompanyLogo(companyId);
            if (resLogo.ok && resLogo.headers.get("Content-Type")?.startsWith("image/")) {
                const blob = await resLogo.blob();
                swapBlobUrl(setLogoBlobUrl, URL.createObjectURL(blob));
                setLogoError(false);
            } else {
                // Logo yoksa 404 gelir; bu bir hata değil "henüz yüklenmemiş" demek.
                swapBlobUrl(setLogoBlobUrl, null);
                setLogoError(resLogo.status !== 404);
            }
        } catch {
            swapBlobUrl(setLogoBlobUrl, null);
            setLogoError(true);
        }

        // Favicon Çekme
        try {
            const resFav = await fetchCompanyFavicon(companyId);
            if (resFav.ok && resFav.headers.get("Content-Type")?.startsWith("image/")) {
                const blob = await resFav.blob();
                swapBlobUrl(setFaviconBlobUrl, URL.createObjectURL(blob));
                setFaviconError(false);
            } else {
                swapBlobUrl(setFaviconBlobUrl, null);
                setFaviconError(resFav.status !== 404);
            }
        } catch {
            swapBlobUrl(setFaviconBlobUrl, null);
            setFaviconError(true);
        }
    };

    const handleImageUpload = async (e, type) => {
        const file = e.target.files[0];
        // Input EN BAŞTA temizleniyor: aşağıdaki erken dönüşlerde de aynı dosyanın
        // tekrar seçilebilmesi gerekiyor. Sonda kalsaydı, doğrulamaya takılan bir
        // dosyayı düzeltip yeniden seçmek mümkün olmazdı (aynı ad, change tetiklenmez).
        e.target.value = "";
        if (!file) return;

        // Sunucuya gitmeden önce ölçü/tip/boyut kontrolü: kural ihlalinde kullanıcı
        // beklemeden ve NEDENİNİ görerek uyarılıyor.
        const validationError = await validateBrandingFile(file, type);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        const setBlobUrl = type === 'logo' ? setLogoBlobUrl : setFaviconBlobUrl;
        const setHasError = type === 'logo' ? setLogoError : setFaviconError;

        // Yükleme sonucu belli olana kadar iki URL de elde tutuluyor:
        //   previewUrl  → kullanıcının seçtiği dosya, anında gösteriliyor
        //   previousUrl → sunucudaki mevcut görsel, hata olursa geri dönülecek
        // Bu yüzden burada revoke YOK; hangisinin serbest bırakılacağı sonuca göre
        // belirleniyor. Önceden hata durumunda önizleme geri alınmıyordu: kullanıcı
        // "yükleme başarısız" uyarısını görürken ekranda yeni görsel duruyordu.
        const previewUrl = URL.createObjectURL(file);
        const previousUrl = type === 'logo' ? logoBlobUrl : faviconBlobUrl;

        setBlobUrl(previewUrl);
        setHasError(false);

        const restorePrevious = () => {
            URL.revokeObjectURL(previewUrl);
            setBlobUrl(previousUrl);
            setHasError(false);
        };

        try {
            const res = await uploadCompanyImage(companyId, type, file);

            if (res.status === 401) {
                restorePrevious();
                navigate("/login");
                return;
            }

            if (res.ok) {
                toast.success(`${BRANDING_LABELS[type]} yüklendi`);
                // Sunucudan taze görsel çekiliyor. Önceden burada sabit 1 saniyelik
                // bir gecikme vardı; "önbelleği atlatmak için" deniyordu ama gecikme
                // önbellek atlatmaz — yalnız yarış durumu yaratıyordu.
                if (previousUrl) URL.revokeObjectURL(previousUrl);
                URL.revokeObjectURL(previewUrl);
                await loadImages();
            } else {
                // Sunucunun gerekçesi gösteriliyor. Önceden burada sabit bir
                // "yükleme başarısız" vardı; hangi kuralın ihlal edildiği kayboluyordu.
                const json = await res.json().catch(() => null);
                const detail = describeFetchError(res, json, `${BRANDING_LABELS[type]} yüklenemedi.`);
                restorePrevious();
                toast.error(detail.message);
            }
        } catch (error) {
            console.error(`${type} upload error:`, error);
            restorePrevious();
            toast.error("Yükleme hatası");
        }
    };

    /**
     * Logo/favicon siler.
     *
     * Sunucu hem dosyayı hem kayıttaki yolu temizliyor; yalnız panel state'ini
     * boşaltmak yetmezdi çünkü görsel klasörden servis ediliyor ve sayfa
     * yenilenince geri gelirdi.
     */
    const handleImageDelete = async (type) => {
        const setBlobUrl = type === 'logo' ? setLogoBlobUrl : setFaviconBlobUrl;
        const setHasError = type === 'logo' ? setLogoError : setFaviconError;
        const currentUrl = type === 'logo' ? logoBlobUrl : faviconBlobUrl;

        try {
            const res = await deleteCompanyImage(companyId, type);

            if (res.status === 401) {
                navigate("/login");
                return;
            }

            if (res.ok) {
                if (currentUrl) URL.revokeObjectURL(currentUrl);
                setBlobUrl(null);
                setHasError(false);
                toast.success(`${BRANDING_LABELS[type]} silindi`);
            } else {
                const json = await res.json().catch(() => null);
                const detail = describeFetchError(res, json, `${BRANDING_LABELS[type]} silinemedi.`);
                toast.error(detail.message);
            }
        } catch (error) {
            console.error(`${type} delete error:`, error);
            toast.error("Silme hatası");
        }
    };

    const downloadLogo = () => {
        if (!logoBlobUrl) return;
        const link = document.createElement("a");
        link.href = logoBlobUrl;
        link.download = `company-${companyId}-logo`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadFavicon = () => {
        if (!faviconBlobUrl) return;
        const link = document.createElement("a");
        link.href = faviconBlobUrl;
        link.download = `company-${companyId}-favicon`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- Formik Yapısı ---
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyName: "",
            contactFullName: "",
            contactEmail: "",
            phoneNumber: "",
            address: "",

            // Fatura Bilgileri
            invoiceTaxOffice: "",
            invoiceTaxNumber: "",
            invoiceTcNumber: "",

            domain: "",

            // Sözleşmeler
            cancellationReturnPrivacy: "",
            personalDataPolicy: "",
            serviceContract: "",
            onlineCollectionContract: "",

            // Ayarlar
            fetchRatesAutomatically: true,
            paymentReminderNotificationsEnabled: true,

            // Otomatik Kur Oranları
            usdMarkupRate: "",
            eurMarkupRate: "",
            gbpMarkupRate: "",

            // Manuel Kur Oranları
            finalUsdRate: "",
            finalEurRate: "",
            finalGbpRate: "",
        },
        validationSchema: Yup.object({
            companyName: Yup.string().required("Gerekli"),
            contactFullName: Yup.string().required("Gerekli"),
            contactEmail: Yup.string().email("Geçersiz e-posta").required("Gerekli"),
            phoneNumber: Yup.string()
                .required("Gerekli")
                .matches(
                    /^(\+90)?\d{10}$/,
                    "Geçersiz telefon numarası. Örnek: +901234567890 veya 0123456789"
                ),
            address: Yup.string().required("Gerekli"),
            domain: Yup.string().required("Gerekli"),

            cancellationReturnPrivacy: Yup.string().max(10000, "En fazla 10 000 karakter"),
            personalDataPolicy: Yup.string().max(10000, "En fazla 10 000 karakter"),
            serviceContract: Yup.string().max(10000, "En fazla 10 000 karakter"),
            onlineCollectionContract: Yup.string().max(10000, "En fazla 10 000 karakter"),

            fetchRatesAutomatically: Yup.boolean(),
            paymentReminderNotificationsEnabled: Yup.boolean(),

            // Otomatik moddayken markup oranları için validasyon
            usdMarkupRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),
            eurMarkupRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),
            gbpMarkupRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),

            // Manuel moddayken final kur değerleri için validasyon
            finalUsdRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),
            finalEurRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),
            finalGbpRate: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .min(0, "Negatif olamaz")
                .nullable(true),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // Sayısal değerleri float'a çevirme ve boş stringleri null yapma
                const payload = {
                    ...values,
                };

                payload.usdMarkupRate = payload.usdMarkupRate ? parseFloat(payload.usdMarkupRate) : null;
                payload.eurMarkupRate = payload.eurMarkupRate ? parseFloat(payload.eurMarkupRate) : null;
                payload.gbpMarkupRate = payload.gbpMarkupRate ? parseFloat(payload.gbpMarkupRate) : null;

                payload.finalUsdRate  = payload.finalUsdRate  ? parseFloat(payload.finalUsdRate)  : null;
                payload.finalEurRate  = payload.finalEurRate  ? parseFloat(payload.finalEurRate)  : null;
                payload.finalGbpRate  = payload.finalGbpRate  ? parseFloat(payload.finalGbpRate)  : null;

                // Boolean'ı garantiye alma
                payload.paymentReminderNotificationsEnabled = values.paymentReminderNotificationsEnabled;

                const res = await updateCompanySettings(companyId, payload);

                if (res.status === 401 || res.statusCode === 401) {
                    navigate("/login");
                    return;
                }

                // Backend'den gelen yanıtın yapısına göre success kontrolü (res.ok fetch wrapper'dan geliyorsa)
                // Eğer doğrudan json dönüyorsan: if (res.success) ...
                // Orijinal kodda: if (res.ok) showToast("success", "Kaydedildi"); else ...
                // requests.js'de fetch wrapper kullanıp res.json() döndüysek 'ok' prop'u olmayabilir.
                // Bu yüzden requests.js'in response yapısına dikkat edilmeli.
                // Burada requests.js'in "response objesi" yerine "json data" döndüğünü varsayarak:

                if (res.statusCode === 200 || res.success || res.data) {
                    toast.success("Kaydedildi");
                } else {
                    toast.error(res.message || "Kaydedilemedi");
                }
            } catch (error) {
                console.error("Save settings error:", error);
                toast.error("Hata oluştu");
            } finally {
                setLoading(false);
            }
        },
    });

    // Bileşen kaldırılırken elde kalan blob URL'lerini serbest bırak. Ayarlar
    // sekmeleri arasında gidip gelmek bu bileşeni defalarca kurup yıkıyor.
    useEffect(() => {
        return () => {
            if (logoBlobUrl) URL.revokeObjectURL(logoBlobUrl);
            if (faviconBlobUrl) URL.revokeObjectURL(faviconBlobUrl);
        };
    }, [logoBlobUrl, faviconBlobUrl]);

    // --- Verileri Getirme ---
    useEffect(() => {
        if (!companyId) return;

        const fetchSettings = async () => {
            try {
                const res = await fetchCompanySettings(companyId);

                if (res.status === 401) {
                    navigate("/login");
                    return;
                }

                // response.json() yapılmış hali geldiyse ve içinde data varsa:
                const json = res;

                if (json.data) {
                    // Kurumsal mı Bireysel mi kontrolü
                    if (json.data.invoiceTcNumber) {
                        setIsCorporate(false);
                    } else {
                        setIsCorporate(true);
                    }

                    formik.setValues({
                        companyName: json.data.companyName || "",
                        contactFullName: json.data.contactFullName || "",
                        contactEmail: json.data.contactEmail || "",
                        phoneNumber: json.data.phoneNumber || "",
                        address: json.data.address || "",
                        domain: json.data.domain || "",

                        invoiceTaxOffice: json.data.invoiceTaxOffice || "",
                        invoiceTaxNumber: json.data.invoiceTaxNumber || "",
                        invoiceTcNumber: json.data.invoiceTcNumber || "",

                        cancellationReturnPrivacy: json.data.cancellationReturnPrivacy || "",
                        personalDataPolicy: json.data.personalDataPolicy || "",
                        serviceContract: json.data.serviceContract || "",
                        onlineCollectionContract: json.data.onlineCollectionContract || "",

                        // Boolean değerlerde undefined kontrolü
                        fetchRatesAutomatically: json.data.fetchRatesAutomatically !== undefined
                            ? json.data.fetchRatesAutomatically
                            : true,
                        paymentReminderNotificationsEnabled: json.data.paymentReminderNotificationsEnabled !== undefined
                            ? json.data.paymentReminderNotificationsEnabled
                            : true,

                        usdMarkupRate: json.data.usdMarkupRate != null ? json.data.usdMarkupRate : "",
                        eurMarkupRate: json.data.eurMarkupRate != null ? json.data.eurMarkupRate : "",
                        gbpMarkupRate: json.data.gbpMarkupRate != null ? json.data.gbpMarkupRate : "",

                        finalUsdRate: json.data.finalUsdRate != null ? json.data.finalUsdRate : "",
                        finalEurRate: json.data.finalEurRate != null ? json.data.finalEurRate : "",
                        finalGbpRate: json.data.finalGbpRate != null ? json.data.finalGbpRate : "",
                    });
                } else {
                    toast.error(json.message || "Ayarlar yüklenemedi");
                }
            } catch (err) {
                console.error("Fetch settings error:", err);
                toast.error("Firma bilgileri yüklenemedi");
            }
        };

        loadImages();
        fetchSettings();
    }, [companyId]);

    // --- Örnek Sözleşme Yükleme ---
    const CONTRACT_TYPE_MAP = {
        cancellationReturnPrivacy: "CANCELLATION_RETURN_PRIVACY",
        personalDataPolicy: "KVKK",
        serviceContract: "SERVICE_AGREEMENT",
        onlineCollectionContract: "ONLINE_COLLECTION",
    };

    const loadSampleContract = async (fieldName) => {
        const type = CONTRACT_TYPE_MAP[fieldName];
        if (!type) return;

        try {
            setSampleLoading(true);
            const json = await fetchSampleContract(type, companyId);

            if (json && json.data) {
                formik.setFieldValue(fieldName, json.data);
                toast.success("Örnek sözleşme yüklendi");
            } else {
                toast.error(json.message || "Örnek sözleşme alınamadı");
            }
        } catch (e) {
            toast.error("Bağlantı hatası");
        } finally {
            setSampleLoading(false);
        }
    };

    return {
        formik,
        loading,
        sampleLoading,
        isCorporate,
        // Logo & Favicon
        logoBlobUrl,
        logoError,
        faviconBlobUrl,
        faviconError,
        handleImageUpload,
        handleImageDelete,
        downloadLogo,
        downloadFavicon,
        // Sözleşme
        loadSampleContract
    };
};